import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/networks/networksApi'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

export interface ApiKeyUser {
  id: string
  name: string
  objectState: string
}

export interface ApiKeyNetwork {
  id: string
  tagId: string
  name: string
  objectState: string
}

export interface ApiKeyAdvertiser {
  id: string
  publicId: string
  name: string
}

export interface ApiKey {
  id: string
  user: ApiKeyUser
  network: ApiKeyNetwork
  name: string
  scopes: string[]
  description: string
  expiry: string
  readOnly: boolean
  createTime: string
  objectState: string
  revocable: boolean
  agentLease: boolean
  expired: boolean
  advertiser: ApiKeyAdvertiser | null
}

function mapApiKey(r: Record<string, unknown>): ApiKey {
  const user = (r.user as Record<string, unknown>) ?? {}
  const network = (r.network as Record<string, unknown>) ?? {}
  const advertiser = r.advertiser as Record<string, unknown> | undefined
  return {
    id: (r.id as string) ?? '',
    user: {
      id: (user.id as string) ?? '',
      name: (user.name as string) ?? '',
      objectState: (user.objectState as string) ?? '',
    },
    network: {
      id: (network.id as string) ?? '',
      tagId: (network.tagId as string) ?? '',
      name: (network.name as string) ?? '',
      objectState: (network.objectState as string) ?? '',
    },
    name: (r.name as string) ?? '',
    scopes: (r.scopes as string[]) ?? [],
    description: (r.description as string) ?? '',
    expiry: (r.expiry as string) ?? '',
    readOnly: (r.readOnly as boolean) ?? false,
    createTime: (r.createTime as string) ?? '',
    objectState: (r.objectState as string) ?? '',
    revocable: (r.revocable as boolean) ?? false,
    agentLease: (r.agentLease as boolean) ?? false,
    expired: (r.expired as boolean) ?? false,
    advertiser: advertiser
      ? {
          id: (advertiser.id as string) ?? '',
          publicId: (advertiser.publicId as string) ?? '',
          name: (advertiser.name as string) ?? '',
        }
      : null,
  }
}

export const ApiKeysApi = {
  getCrossNetwork: async (scope: string): Promise<ApiKey[]> => {
    const params = new URLSearchParams({
      context: getContext(),
      scope,
      includeExpired: 'false',
      includeHidden: 'false',
      includeInactive: 'false',
    })
    const res = await apiFetch(`${API_BASE}/apikeys/crossnetwork?${params}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch cross-network API keys')
    const data = await res.json()
    const results = toArray(data)
    return results.map(mapApiKey)
  },
}
